/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
*/

import { EmployeeViewTypes, ScheduleCalendarType, ShiftFilterType, ShiftFilterTypes } from "sh-models";
import { MemberDataService, ScheduleFiltersDataService, TagDataService } from "sh-services";

import { clearFilteredMembers } from "../mutators/filter/clearFilteredMembers";
import clearFilteredTags from "../mutators/filter/clearFilteredTags";
import { setMemberFilter } from "../mutators/setMemberFilter";
import { setSelectedShiftFilter } from "../mutators/setSelectedShiftFilter";
import { setShowProfilePicturesView } from "../mutators/setShowProfilePicturesView";
import { setStaffPerIntervalEnabled } from "../mutators/setStaffPerIntervalEnabled";
import { setTagFilter } from "../mutators/setTagFilter";
import { SchedulesViewStateStore } from "../store/schema/SchedulesViewStateStore";
import updateScheduleViewDimensions from "../actions/updateScheduleViewDimensions";
import UrlFactory from "../../../../utility/UrlFactory";
import { Moment } from "moment";
import { ScheduleGridUtils } from "../utils/ScheduleGridUtils";

/**
 * Load the team's latest used filters from cache and sets in store.
 * @param viewState The view state.
 * @param teamId The team id.
 */
export const loadAndSetTeamLatestUsedFilters = async (viewState: SchedulesViewStateStore, teamId: string): Promise<void> => {
    const scheduleFilters = await ScheduleFiltersDataService.getScheduleFilters(teamId);

    if (scheduleFilters) {
        const { filters, scheduleView, view } = scheduleFilters;
        const { groups, members, shifts } = filters;
        const { quickAccess, show, viewBy } = view;
        let scheduleCalendarType: ScheduleCalendarType;
        let selectedDate: Moment;

        // clear the existing group and member filters
        clearFilteredTags();
        clearFilteredMembers();

        // override previous team filters with deeplink info
        if (UrlFactory.isSchedulesPageDeepLink(window.location)) {
            scheduleCalendarType = UrlFactory.getScheduleTypeFromDeepLink(window.location);
            selectedDate = UrlFactory.getScheduleSelectedDateFromDeepLink(window.location);
        }

        /** updateScheduleViewDimensions used instead of updating viewState or SchedulesViewStateStore() directly.
         * This is because updating them on its own will not trigger a re-render for the schedule grid component.
         * updateScheduleViewDimensions() updates viewState and triggers a re-render. */

        // Uses instrumentViewsDataAfterLoad to instrument Schedule views for different employee view types.
        const instrumentViewsDataAfterLoad = viewState.employeeViewType === EmployeeViewTypes.TeamShiftsView ? ScheduleGridUtils.isLargeTeam() : false;
        updateScheduleViewDimensions(
            viewState,
            false, /* isTeamBootstrap */
            quickAccess, /* employeeViewType */
            scheduleCalendarType ?? scheduleView, /* scheduleCalendarType */
            viewBy, /* scheduleOverViewType */
            selectedDate ?? null, /* selectedDate */
            show.groups, /* isViewGrouped */
            instrumentViewsDataAfterLoad /*instrument views data*/
        );
        setShowProfilePicturesView(show.showProfilePictures ?? true);
        setStaffPerIntervalEnabled({ value: show.staffPerTimeInterval });

        // set the selected groups
        const allGroups = await TagDataService.getTags(teamId);
        allGroups.forEach(group => {
            if (groups?.includes(group.id)) {
                setTagFilter(group, true);
            }
        });

        // set the selected members
        members?.forEach(async memberId => {
            const member = await MemberDataService.getMemberByIdFromCurrentTeam(memberId);

            if (member) {
                setMemberFilter(member, true);
            }
        });

        // set the selected shift filters
        const shiftFilters = new Set<ShiftFilterType>(shifts ?? []);
        shiftFilters.forEach(shiftFilter => {
            shiftFilters.add(shiftFilter);
        });

        setSelectedShiftFilter(ShiftFilterTypes.ActiveShifts, shiftFilters.has(ShiftFilterTypes.ActiveShifts));
        setSelectedShiftFilter(ShiftFilterTypes.ConflictingShifts, shiftFilters.has(ShiftFilterTypes.ConflictingShifts));
        setSelectedShiftFilter(ShiftFilterTypes.TimeoffShifts, shiftFilters.has(ShiftFilterTypes.TimeoffShifts));
    }
};
